// @ts-nocheck
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Search, ArrowRight, ArrowLeft, Loader2, CheckCircle2,
  ShieldCheck, Plug, Sparkles, AlertTriangle,
  Mail, Calendar, FolderOpen, Code, MessageSquare, Briefcase,
  BarChart3, CreditCard, ListChecks, Palette,
} from 'lucide-react';
import { BRAND } from '../../../constants/brand';
import {
  PROVIDER_CATEGORIES,
  resolveProvider,
  type ProviderInfo,
} from '../providerCatalog';
import { getPermissionContract } from '../providerPermissions';
import { appIconUrl } from '../../dossier/AppIcon';
import { useComposioConnectFlow } from './useComposioConnectFlow';
import type { ConnectionRow } from '../ConnectedAppsSection';

type HubStep = 'browse' | 'permissions' | 'connecting' | 'success';
type HubTab = 'add' | 'connected';

const ICON_MAP: Record<string, React.ElementType> = {
  mail: Mail,
  calendar: Calendar,
  folder: FolderOpen,
  code: Code,
  chat: MessageSquare,
  crm: Briefcase,
  analytics: BarChart3,
  payments: CreditCard,
  tasks: ListChecks,
  design: Palette,
};

function ProviderLogo({ provider, size = 32 }: { provider: ProviderInfo; size?: number }) {
  const [failed, setFailed] = useState(false);
  const url = appIconUrl(provider.slug);
  if (url && !failed) {
    return (
      <span className="grid shrink-0 place-items-center rounded-tile border border-line bg-white" style={{ width: size, height: size }} aria-hidden>
        <img src={url} alt="" width={Math.round(size * 0.55)} height={Math.round(size * 0.55)} className="object-contain" onError={() => setFailed(true)} />
      </span>
    );
  }
  return (
    <div
      className="flex items-center justify-center rounded-tile font-bold shrink-0"
      style={{ width: size, height: size, fontSize: size * 0.42, background: provider.color, color: provider.textColor || '#FFFFFF' }}
      aria-hidden
    >
      {provider.initial}
    </div>
  );
}


export interface AppConnectHubProps {
  open: boolean;
  onClose: () => void;
  clientId: string;
  connections: ConnectionRow[];
  suggestedSlugs?: string[];
  onConnected: () => void;
  initialProvider?: string | null;
  initialTab?: HubTab;
}

const AppConnectHub: React.FC<AppConnectHubProps> = ({
  open,
  onClose,
  clientId,
  connections,
  suggestedSlugs = [],
  onConnected,
  initialProvider,
  initialTab = 'add',
}) => {
  const [tab, setTab] = useState<HubTab>(initialTab);
  const [step, setStep] = useState<HubStep>('browse');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<ProviderInfo | null>(null);
  const [successProvider, setSuccessProvider] = useState<ProviderInfo | null>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  const { runConnectFlow, cancelConnectFlow, connectingSlug, connectError, clearConnectError } = useComposioConnectFlow(clientId, () => {
    onConnected();
  });

  const connectedSlugs = useMemo(() => new Set(
    (connections || [])
      .filter(c => {
        const s = String(c.status || '').toLowerCase();
        return s === 'connected' || s === 'active';
      })
      .map(c => c.provider.toLowerCase()),
  ), [connections]);

  const connectedRows = useMemo(() =>
    (connections || [])
      .filter(c => {
        const s = String(c.status || '').toLowerCase();
        return s === 'connected' || s === 'active';
      })
      .map(row => ({ row, ...resolveProvider(row.provider) })),
  [connections]);

  const suggestedProviders = useMemo(() =>
    suggestedSlugs
      .map(slug => resolveProvider(slug).provider)
      .filter(p => !connectedSlugs.has(p.slug)),
  [suggestedSlugs, connectedSlugs]);

  const pickerCategories = useMemo(() => {
    const q = search.trim().toLowerCase();
    return PROVIDER_CATEGORIES.map(cat => ({
      ...cat,
      providers: cat.providers.filter(p =>
        !connectedSlugs.has(p.slug) &&
        (!q || p.label.toLowerCase().includes(q) || p.slug.includes(q) || cat.label.toLowerCase().includes(q)),
      ),
    })).filter(cat => cat.providers.length > 0);
  }, [search, connectedSlugs]);

  const resetState = useCallback(() => {
    setStep('browse');
    setTab(initialTab);
    setSearch('');
    setSelected(null);
    setSuccessProvider(null);
    clearConnectError();
  }, [initialTab, clearConnectError]);

  const handleClose = useCallback(() => {
    // Don't trap the user mid-connect — cancel the in-flight flow (closes the
    // popup + tears down listeners), then let the modal close normally.
    if (connectingSlug) cancelConnectFlow();
    resetState();
    onClose();
  }, [connectingSlug, cancelConnectFlow, onClose, resetState]);

  useLayoutEffect(() => {
    if (!open) return;
    setTab(initialTab);
    if (initialProvider) {
      const { provider } = resolveProvider(initialProvider);
      if (!connectedSlugs.has(provider.slug)) {
        setSelected(provider);
        setStep('permissions');
      } else {
        setSelected(null);
        setTab('connected');
        setStep('browse');
      }
    } else {
      setStep('browse');
      setSelected(null);
    }
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [open, initialProvider, initialTab, connectedSlugs]);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [step, tab]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, connectingSlug, handleClose]);

  const pickProvider = useCallback((provider: ProviderInfo) => {
    clearConnectError();
    setSelected(provider);
    setStep('permissions');
  }, [clearConnectError]);

  const authorize = useCallback(async () => {
    if (!selected) return;
    setStep('connecting');
    const ok = await runConnectFlow(selected, {
      onSuccess: slug => {
        setSuccessProvider(resolveProvider(slug).provider);
        setStep('success');
      },
    });
    if (!ok) setStep('permissions');
  }, [selected, runConnectFlow]);

  const contract = selected ? getPermissionContract(selected.slug) : null;

  // Render through a portal to <body>. The host (My Business → ConnectedApps)
  // sits inside an ancestor with a transform/filter/will-change, which makes
  // THAT ancestor the containing block for `position: fixed` — so `inset-0`
  // resolves to the ancestor's box (offset below the page header) instead of
  // the viewport, leaving the backdrop's "space from the top". Portaling to
  // body guarantees the overlay is viewport-relative, full-bleed top-to-bottom.
  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[200] flex items-center justify-center overflow-y-auto overscroll-contain p-4 sm:p-6"
          style={{ background: 'rgba(15, 23, 42, 0.52)', backdropFilter: 'blur(8px)' }}
          onClick={handleClose}
          role="dialog"
          aria-modal="true"
          aria-label="Connect apps"
        >
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            onClick={e => e.stopPropagation()}
            className="relative w-full max-w-[760px] flex flex-col rounded-panel overflow-hidden shadow-2xl"
            style={{
              background: '#FFFFFF',
              // Cap to the viewport MINUS the overlay padding (p-6 = 24px each
              // side = 48px) so the modal + its margins always fit on screen.
              // The old `min(88vh,820px)` + py-10 (80px) overflowed the viewport
              // on shorter screens → a page-level scrollbar + the modal pushed
              // down with a scrollable gap above it (the "space from top").
              maxHeight: 'min(calc(100vh - 48px), 820px)',
              boxShadow: '0 24px 80px rgba(17,24,39,0.22), 0 0 0 1px rgba(24,25,29,0.08)',
            }}
          >
            {/* Header: the same shape as every other window in the product. */}
            <div className="relative px-6 pt-5 pb-4 shrink-0 border-b border-line bg-white">
              <div className="flex items-start justify-between gap-4 relative">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="w-11 h-11 rounded-card grid place-items-center shrink-0 bg-ink text-white">
                    <Plug className="w-5 h-5" strokeWidth={2.25} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-dim mb-0.5">
                      Connectors
                    </div>
                    <h2 className="text-[20px] font-bold text-ink tracking-tight leading-tight">
                      {step === 'permissions' && selected
                        ? `Connect ${selected.label}`
                        : step === 'connecting' && selected
                          ? `Authorizing ${selected.label}…`
                          : step === 'success' && successProvider
                            ? `${successProvider.label} is connected`
                            : 'Connect your tools'}
                    </h2>
                    <p className="text-[13px] text-ink-dim mt-1 max-w-md leading-snug">
                      {step === 'browse' && 'Sign in with the app itself. You see what it can read before anything connects.'}
                      {step === 'permissions' && 'Review what access you are granting. Nothing connects until you authorize.'}
                      {step === 'connecting' && 'Finish sign-in in the popup window. This page updates automatically.'}
                      {step === 'success' && 'Your app is linked and ready. Orbit can now read from it when you ask.'}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleClose}
                  className="shrink-0 p-2 rounded-card text-ink-soft hover:text-ink hover:bg-white/80 transition-colors disabled:opacity-40"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {step === 'browse' && (
                <div role="tablist" aria-label="Apps" className="flex gap-1.5 mt-5">
                  {(['add', 'connected'] as const).map(id => (
                    <button
                      key={id}
                      type="button"
                      role="tab"
                      aria-selected={tab === id}
                      onClick={() => setTab(id)}
                      className={`rounded-act px-3 py-1.5 text-[13px] transition-colors ${tab === id ? 'bg-canvas-inset font-semibold text-ink' : 'font-medium text-ink-muted hover:bg-canvas-inset hover:text-ink'}`}
                    >
                      {id === 'add' ? 'Ready to connect' : `Connected${connectedRows.length > 0 ? ` · ${connectedRows.length}` : ''}`}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Body — sizes to content; scrolls only when catalog is long.
                No hardcoded maxHeight: the modal is flex-col capped at
                min(88vh,820px), header/footer are shrink-0, so this body
                (flex-shrink:1 + min-h-0) fills the remaining space exactly and
                scrolls only when it overflows. The old magic `- 168px` offset
                didn't match the (shorter) permissions header, which overflowed
                on first paint → the "scrollbar then corrects" flash. */}
            <div
              ref={bodyRef}
              className="overflow-x-hidden overflow-y-auto overscroll-contain min-h-0"
            >
              <AnimatePresence mode="wait" initial={false}>
                {step === 'browse' && tab === 'add' && (
                  <motion.div
                    key="browse-add"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    className="px-6 py-5 space-y-5"
                  >
                    <div className="flex items-center gap-2.5 bg-canvas rounded-card px-3.5 py-2.5 ring-1 ring-line/80">
                      <Search className="w-4 h-4 text-ink-soft shrink-0" />
                      <input
                        type="text"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Search apps"
                        className="flex-1 bg-transparent text-[14px] outline-none placeholder:text-ink-soft"
                        autoFocus
                      />
                    </div>

                    {suggestedProviders.length > 0 && !search.trim() && (
                      <div>
                        <div className="flex items-center gap-2 mb-3">
                          <Sparkles className="w-3.5 h-3.5 text-brand-500" />
                          <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-dim">
                            From your stack
                          </span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                          {suggestedProviders.map(provider => (
                            <button
                              key={`suggest-${provider.slug}`}
                              type="button"
                              onClick={() => pickProvider(provider)}
                              className="flex items-center gap-3 p-3.5 rounded-card text-left transition-all hover:scale-[1.01] active:scale-[0.99]"
                              style={{ border: '1px solid rgba(24,25,29,0.2)', background: 'white' }}
                            >
                              <ProviderLogo provider={provider} size={36} />
                              <div className="flex-1 min-w-0">
                                <div className="text-[14px] font-semibold text-ink">{provider.label}</div>
                                <div className="text-[12px] text-brand-600 font-medium mt-0.5">Recommended · Connect</div>
                              </div>
                              <ArrowRight className="w-4 h-4 text-brand-500 shrink-0" />
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {pickerCategories.length === 0 ? (
                      <div className="text-center py-14 text-[14px] text-ink-dim">
                        {search.trim()
                          ? `No apps match "${search}". Try another name.`
                          : 'All available apps are already connected.'}
                      </div>
                    ) : (
                      pickerCategories.map(cat => {
                        const Icon = ICON_MAP[cat.iconKey] || ListChecks;
                        return (
                          <div key={cat.id}>
                            <div className="flex items-center gap-2 mb-3">
                              <Icon className="w-3.5 h-3.5 text-ink-soft" />
                              <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-dim">
                                {cat.label}
                              </span>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                              {cat.providers.map(provider => (
                                <button
                                  key={provider.slug}
                                  type="button"
                                  onClick={() => pickProvider(provider)}
                                  className="flex items-center gap-3 p-3 rounded-card border bg-white text-left hover:bg-canvas/80 transition-colors group"
                                  style={{ borderColor: 'rgba(17,24,39,0.08)' }}
                                >
                                  <ProviderLogo provider={provider} size={32} />
                                  <span className="flex-1 text-[13.5px] font-semibold text-ink">{provider.label}</span>
                                  <ArrowRight className="w-3.5 h-3.5 text-ink-soft group-hover:text-brand-500 transition-colors" />
                                </button>
                              ))}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </motion.div>
                )}

                {step === 'browse' && tab === 'connected' && (
                  <motion.div
                    key="browse-connected"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    className="px-6 py-5"
                  >
                    {connectedRows.length === 0 ? (
                      <div className="text-center py-16">
                        <div className="w-14 h-14 rounded-panel bg-brand-50 grid place-items-center mx-auto mb-4">
                          <Plug className="w-6 h-6 text-brand-500" />
                        </div>
                        <p className="text-[15px] font-medium text-ink mb-1">No apps connected yet</p>
                        <p className="text-[13px] text-ink-dim mb-5">Connect your first tool to get started.</p>
                        <button
                          type="button"
                          onClick={() => setTab('add')}
                          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-card text-[13px] font-semibold text-white whitespace-nowrap"
                          style={{ background: BRAND.blue }}
                        >
                          Browse apps
                          <ArrowRight className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                        {connectedRows.map(({ row, provider }) => (
                          <div
                            key={row.id}
                            className="flex items-center gap-3 p-3.5 rounded-card border bg-white"
                            style={{ borderColor: 'rgba(22,163,74,0.15)', background: BRAND.greenSoft + '44' }}
                          >
                            <ProviderLogo provider={provider} size={36} />
                            <div className="flex-1 min-w-0">
                              <div className="text-[14px] font-semibold text-ink">{provider.label}</div>
                              <div className="text-[12px] text-green-700 font-medium flex items-center gap-1.5 mt-0.5">
                                <span className="w-1.5 h-1.5 rounded-full bg-green-600" />
                                Connected
                              </div>
                            </div>
                            <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" />
                          </div>
                        ))}
                      </div>
                    )}
                  </motion.div>
                )}

                {step === 'permissions' && selected && contract && (
                  <motion.div
                    key="permissions"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    className="px-6 py-4 space-y-3.5"
                  >
                    <button
                      type="button"
                      onClick={() => { setStep('browse'); setSelected(null); }}
                      className="inline-flex items-center gap-1.5 text-[13px] font-medium text-ink-dim hover:text-ink mb-1"
                    >
                      <ArrowLeft className="w-3.5 h-3.5" />
                      Back to apps
                    </button>

                    <div className="flex items-center gap-3 p-4 rounded-panel" style={{ background: `${contract.accent}0D` }}>
                      <ProviderLogo provider={selected} size={48} />
                      <div>
                        <div className="text-[16px] font-semibold text-ink">{contract.displayName}</div>
                        <div className="text-[13px] text-ink-muted mt-0.5">{contract.caption}</div>
                      </div>
                    </div>

                    {connectError && (
                      <div
                        className="flex items-start gap-2.5 rounded-card px-3.5 py-3 text-[13px] leading-snug"
                        style={{ background: 'rgba(220,38,38,0.06)', border: '1px solid rgba(220,38,38,0.18)', color: '#B91C1C' }}
                        role="alert"
                      >
                        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold">Couldn’t start the connection</div>
                          <div className="mt-0.5 text-[12.5px] break-words" style={{ color: '#9F1239' }}>{connectError}</div>
                        </div>
                      </div>
                    )}

                    {/* Compact trust line — replaces the verbose We-will-see /
                        write / never boxes. The essentials in one breath. */}
                    <p className="flex items-start gap-2 text-[12.5px] text-ink-dim leading-relaxed">
                      <ShieldCheck className="w-4 h-4 shrink-0 mt-px" style={{ color: contract.accent }} />
                      <span>
                        <span className="text-ink font-medium">Read-only by default.</span>{' '}
                        Orbit never deletes, shares, or trains on your data, and never acts without
                        your say-so. Revoke anytime from Connected Apps.
                      </span>
                    </p>
                  </motion.div>
                )}

                {step === 'connecting' && selected && (
                  <motion.div
                    key="connecting"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex flex-col items-center justify-center text-center px-6 py-16"
                  >
                    <div className="relative mb-5">
                      <ProviderLogo provider={selected} size={64} />
                      <div className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-white grid place-items-center shadow-md">
                        <Loader2 className="w-4 h-4 animate-spin text-brand-500" />
                      </div>
                    </div>
                    <h3 className="text-[17px] font-semibold text-ink mb-2">Waiting for authorization</h3>
                    <p className="text-[13px] text-ink-dim max-w-sm leading-relaxed">
                      Complete the sign-in in the popup. If it did not open, check your browser's popup blocker.
                    </p>
                  </motion.div>
                )}

                {step === 'success' && successProvider && (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, scale: 0.98 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="flex flex-col items-center justify-center text-center px-6 py-16"
                  >
                    <div className="w-16 h-16 rounded-full bg-green-100 grid place-items-center mb-5">
                      <CheckCircle2 className="w-8 h-8 text-green-600" />
                    </div>
                    <div className="flex items-center gap-3 mb-3">
                      <ProviderLogo provider={successProvider} size={40} />
                      <h3 className="text-[18px] font-semibold text-ink">{successProvider.label} connected</h3>
                    </div>
                    <p className="text-[13px] text-ink-dim max-w-sm">
                      You are all set. This app now appears under Connected on your business profile.
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Footer */}
            <div
              className="shrink-0 px-6 py-3.5 flex items-center justify-between gap-3 border-t border-line-soft"
              style={{ background: 'rgba(250,250,250,0.92)' }}
            >
              <div className="flex items-center gap-2 ml-auto">
                {step === 'permissions' && selected && contract && (
                  <>
                    <button
                      type="button"
                      onClick={() => { setStep('browse'); setSelected(null); }}
                      className="px-4 py-2.5 rounded-card text-[13px] font-medium text-ink-muted hover:bg-canvas-inset"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={authorize}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-card text-[13px] font-semibold text-white transition-all hover:brightness-105 whitespace-nowrap"
                      style={{
                        background: contract.accent,
                        boxShadow: `0 4px 16px ${contract.accent}40`,
                      }}
                    >
                      Authorize {contract.displayName}
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </>
                )}
                {step === 'success' && (
                  <>
                    <button
                      type="button"
                      onClick={() => { setStep('browse'); setTab('add'); setSuccessProvider(null); setSelected(null); }}
                      className="px-4 py-2.5 rounded-card text-[13px] font-medium text-ink-muted hover:bg-canvas-inset"
                    >
                      Connect another
                    </button>
                    <button
                      type="button"
                      onClick={handleClose}
                      className="px-5 py-2.5 rounded-card text-[13px] font-semibold text-white"
                      style={{ background: BRAND.blue }}
                    >
                      Done
                    </button>
                  </>
                )}
                {step === 'connecting' && (
                  <button
                    type="button"
                    onClick={handleClose}
                    className="px-5 py-2.5 rounded-card text-[13px] font-medium text-ink-muted hover:bg-canvas-inset"
                  >
                    Cancel
                  </button>
                )}
                {step === 'browse' && (
                  <button
                    type="button"
                    onClick={handleClose}
                    className="px-5 py-2.5 rounded-card text-[13px] font-medium text-ink-muted hover:bg-canvas-inset"
                  >
                    Close
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default AppConnectHub;
